import { useQueryClient } from "@tanstack/react-query";
import { Sidebar, TextArea, BaseButton } from "@velocity-ehs/component-library";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import UsersService from "@velocity-ehs/user-utils-module";
import EhsConfigService from "../../../services/EhsConfigService";
import { SnackbarType } from "../../sharedComponents/Snackbar";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../../../context/SnackbarContext";
import { useEffect } from "react";
import classes from "./ConfigFileSliders.module.scss";

interface Props {
  showSlider: boolean;
  selectedConfig: any;
  closeSliderCallback?: () => void;
}

const UpdateConfigFileSlider: React.FC<Props> = ({
  showSlider,
  selectedConfig,
  closeSliderCallback,
}) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbarActionContext();

  const formMethods = useForm({
    mode: "onBlur",
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting },
  } = formMethods;

  useEffect(() => {
    if (showSlider) {
      setValue("description", selectedConfig?.description);
    }
    // eslint-disable-next-line
  }, [showSlider]);

  const queryClient = useQueryClient();

  const onSubmit = async (values: any) => {
    const { description } = values;

    try {
      await EhsConfigService.updateConfigFile({
        id: selectedConfig?.id,
        description: description,
        customerId: UsersService.customer.id,
      });

      queryClient.invalidateQueries({ queryKey: ["ehsConfigurations"] });

      onClose();
      showSnackbar(
        SnackbarType.Success,
        t("upload_configuration_update_success")
      );
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    }
  };

  const onClose = () => {
    reset();
    closeSliderCallback();
  };

  const footerButtons = () => {
    return (
      <>
        <BaseButton
          type="submit"
          label={t("universal_Save")}
          loading={isSubmitting}
          form="ehsConfigurationUpdate"
          className={classes.saveButton}
        />
        <BaseButton
          label={t("universal_Cancel")}
          className="p-button-text"
          onClick={onClose}
        />
      </>
    );
  };

  return (
    <Sidebar
      header={t("upload_configuration_update_description_title")}
      visible={showSlider}
      onHide={onClose}
      footer={footerButtons()}
    >
      <form id="ehsConfigurationUpdate" onSubmit={handleSubmit(onSubmit)}>
        <TextArea
          name="description"
          control={control}
          label={t("universal_Description")}
        />
      </form>
    </Sidebar>
  );
};

export { UpdateConfigFileSlider };
